import type { HotEditElementKind } from './elementKind';
import type { HotEditListContext } from './listContext';

/** Prefix for all hot-edit postMessage envelopes between host and preview iframe. */
export const IDEOZ_EDIT_MSG_PREFIX = 'IDEOZ_EDIT_' as const;

export const IDEOZ_PREVIEW_BRIDGE_VERSION = 3 as const;

/** Messages host → iframe */
export type HostToIframeEditMessage =
  | { type: 'IDEOZ_EDIT_ENABLE'; enabled: boolean }
  | { type: 'IDEOZ_EDIT_PING'; requestId: string }
  | {
      type: 'IDEOZ_EDIT_APPLY';
      elementId: string;
      patch: StylePatch;
    }
  | {
      type: 'IDEOZ_EDIT_CLEAR_INLINE';
      elementId: string;
      keys?: string[];
    }
  /** Host → iframe: pointer left preview bounds — drop stale hover outline */
  | { type: 'IDEOZ_EDIT_CLEAR_HOVER' };

/** Messages iframe → host */
export type IframeToHostEditMessage =
  | {
      type: 'IDEOZ_EDIT_READY';
      pathname: string;
      version?: number;
    }
  | { type: 'IDEOZ_EDIT_PONG'; requestId: string }
  | {
      type: 'IDEOZ_EDIT_HOVER';
      elementId: string | null;
      rect: DOMRectLike | null;
      tagName: string | null;
    }
  | {
      type: 'IDEOZ_EDIT_SELECT';
      elementId: string;
      rect: DOMRectLike;
      tagName: string;
      /** PascalCase React component owning this DOM node (e.g. CardTitle), when hit target is DS output */
      ownerComponentName?: string;
      source?: ElementDebugSource | null;
      /** Deepest JSX source (often component implementation — for debugging only). */
      implementationSource?: ElementDebugSource | null;
      selectorPath?: string;
      /** When selection is inside a repeated template (`.map`), disambiguates which instance to patch */
      listContext?: HotEditListContext | null;
      computedStyles: ComputedStylePayload;
      textContent: string;
      className: string;
      /** True when element has element children (complex DOM tree); text edits risky */
      hasChildElements: boolean;
      elementKind?: HotEditElementKind;
      editTargetKind?: 'callSite' | 'designSystemOnly' | 'missing';
      canPersistClasses?: boolean;
      blockReason?: string | null;
      inputPlaceholder?: string;
      inputValue?: string;
      inputType?: string;
      optionValues?: string[];
    }
  | { type: 'IDEOZ_EDIT_ROUTE'; pathname: string }
  | { type: 'IDEOZ_EDIT_DESELECT' }
  | { type: 'IDEOZ_EDIT_HMR_IDLE'; pathname: string };

export type DOMRectLike = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
};

export type ElementDebugSource = {
  filePath: string;
  lineNumber: number;
  columnNumber: number;
};

export type StylePatch =
  | { kind: 'style'; property: string; value: string }
  | { kind: 'className'; add?: string[]; remove?: string[] }
  | { kind: 'text'; value: string }
  | { kind: 'dom'; key: string; value: string };

/** Snapshot of computed styles we expose to the Tweaks panel (camelCase CSS keys). */
export type ComputedStylePayload = {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  textAlign?: string;
  lineHeight?: string;
  letterSpacing?: string;
  width?: string;
  height?: string;
  opacity?: string;
  backgroundColor?: string;
  gap?: string;
  flexDirection?: string;
  justifyContent?: string;
  alignItems?: string;
  display?: string;
  objectFit?: string;
  /** SVG / icon approximate colors */
  fill?: string;
  stroke?: string;
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
  borderTopWidth?: string;
  borderRightWidth?: string;
  borderBottomWidth?: string;
  borderLeftWidth?: string;
  borderStyle?: string;
  borderTopColor?: string;
  borderRadius?: string;
};

export type UnknownEditEnvelope =
  | HostToIframeEditMessage
  | IframeToHostEditMessage
  | Record<string, unknown>;

export function isIdeozEditMessage(data: unknown): data is { type: string } & Record<string, unknown> {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    typeof (data as { type: unknown }).type === 'string' &&
    String((data as { type: string }).type).startsWith(IDEOZ_EDIT_MSG_PREFIX)
  );
}
